<template>
  <div class="m-ma-dev">
    <!-- ==================== ① 表未就绪（200 + ready:false，不是报错） ==================== -->
    <div v-if="meta && meta.ready === false" class="card not-ready">
      <div class="nr-t">⚠️ 数据未就绪</div>
      <p>{{ meta.reason }}</p>
      <p>
        本页是<b>只读页</b>：均线由本地脚本算好后同步到服务器（<code>ma_dev_base</code>），线上不做实时计算
        —— 它需要的<b>后复权长历史</b>在线上不存在。请先跑
        <code>build-ma-dev.js</code> + <code>sync-ma-dev.sh</code>。
      </p>
    </div>

    <template v-else>
      <!-- ==================== ② 均线三档 + 小入口（提醒 / 详情 / 筛选） ==================== -->
      <div class="toolbar">
        <span class="seg" id="mdev-win-seg">
          <button
            v-for="w in windows"
            :key="'w' + w"
            type="button"
            :class="{ on: w === win }"
            :data-win="w"
            @click="setWindow(w)"
          >{{ winShort[w] }}</button>
        </span>
        <span class="sp" />
        <button type="button" class="tip-ico" aria-label="研究结论提醒" @click="showTip = true">⚠</button>
        <button type="button" class="doc-ico" @click="showCaliber = true">详情</button>
        <button type="button" class="flt" @click="showFilter = true">筛选</button>
      </div>

      <!-- 研究结论提醒（短弹层） -->
      <van-popup v-model="showTip" position="bottom" round :style="{ maxHeight: '70%' }">
        <div class="tip-pop">
          <div class="tip-pop-h">
            <span>研究结论</span>
            <van-icon name="cross" @click="showTip = false" />
          </div>
          <div class="tip-pop-b">
            <template v-if="excessMeasured">
              <p>
                <b>「贴近均线」不是买点</b> —— 甜蜜区 −3%~+3% 实测超额
                <b>{{ sweetText }}</b>，全部约等于 0，且低于双边成本 0.08%。
              </p>
              <p>
                真正有结构的是「偏离越负越好」：深度跌破档为正、严重追高档是最稳避雷信号。
                这层结构与库内 <code>rev60</code> 同源 —— 若真要赚这个钱，用反转因子更直接。
              </p>
            </template>
            <template v-else>
              <p>
                当前为 <b>{{ winLabel }}</b>：该窗口<b>没做过回测</b> ⇒ 本页不对它做超额断言，
                「实测超额」列显示「未测」。要看超额请切到半年线 / 年线。
              </p>
            </template>
            <button type="button" class="tip-more" @click="openCaliberFromTip">查看完整口径与结论 ›</button>
          </div>
        </div>
      </van-popup>

      <!-- ==================== ③ 排序两端 ==================== -->
      <div class="sortbar">
        <span class="seg2" id="mdev-sort-seg">
          <button
            type="button"
            :class="{ on: sort === 'dev_asc' }"
            data-sort="dev_asc"
            @click="setSort('dev_asc')"
          >跌得最深</button>
          <button
            type="button"
            :class="{ on: sort === 'dev_desc' }"
            data-sort="dev_desc"
            @click="setSort('dev_desc')"
          >涨得最远</button>
        </span>
        <span class="cnt">共 {{ nf(stats.count) }} 只 · {{ winShort[win] }}</span>
      </div>

      <!-- ==================== ④ 数据概览（默认折叠，与 PC 端一致） ==================== -->
      <div class="ov">
        <button type="button" class="ov-head" @click="showOverview = !showOverview">
          <span>数据概览</span>
          <van-icon :name="showOverview ? 'arrow-up' : 'arrow-down'" />
        </button>
        <div v-show="showOverview" class="ov-body">
          <div class="grid2">
            <div class="stat">
              <div class="lb">池内家数</div>
              <div class="vl">{{ nf(stats.count) }}<small>只</small></div>
              <div class="sb">{{ sampleLabel }} · 已剔 ST <b>{{ nf(stats.stCount) }}</b> 只</div>
            </div>
            <div class="stat">
              <div class="lb">偏离中位数（{{ winShort[win] }}）</div>
              <div class="vl" :class="clsOf(stats.medianDev)">{{ pct(stats.medianDev) }}</div>
              <div class="sb">均值 {{ pct(stats.meanDev) }} · 负值 = 在均线下方</div>
            </div>
            <div class="stat">
              <div class="lb">深度跌破（≤ −35%）</div>
              <div class="vl up">{{ nf(deepN) }}<small>只</small></div>
              <div class="sb">该档实测 <b>{{ excessText(1) }}</b></div>
            </div>
            <div class="stat alert">
              <div class="lb">严重追高（&gt; +50%）</div>
              <div class="vl down">{{ nf(stats.chaseCount) }}<small>只</small></div>
              <div class="sb">
                该档实测 <b>{{ excessText(12) }}</b>
                <template v-if="excessMeasured"> ⇒ 避雷</template>
              </div>
            </div>
          </div>

          <div class="distmini">
            <div class="bar" id="mdev-dist-bar">
              <i
                v-for="(n, i) in dist"
                :key="'d' + i"
                :class="i <= 1 ? 'hot' : (i === 12 ? 'chase' : '')"
                :style="{ height: barH(n) }"
              />
            </div>
            <div class="cap">
              13 档分布（左 = 跌得最深 → 右 = 涨得最远）·
              <span class="muted">
                「均线附近」<b>{{ nf(stats.nearCount) }}</b> 只，占 <b>{{ sharePct(stats.nearShare) }}</b>
                <template v-if="excessMeasured"> —— 该档实测 <b>{{ excessText(6) }}</b>，全档最没信息</template>
                <template v-else> —— 该窗口未做回测，不做断言</template>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- ==================== ⑤ 样本列表 ==================== -->
      <div class="sec-h">
        <h3>样本列表</h3>
        <span class="hint">{{ listHint }}</span>
        <span class="sp" />
        <button type="button" class="act" @click="showBacktest = true">分档表</button>
      </div>

      <div v-if="error" class="errbar">{{ error }}</div>
      <van-loading v-else-if="loading && !rows.length" class="pl" vertical>正在读取年线偏离数据…</van-loading>
      <van-empty v-else-if="!loading && !rows.length" :description="emptyText" />

      <template v-else>
        <div class="card" id="mdev-list">
          <div
            v-for="r in rows"
            :key="r.code"
            class="stock"
            :class="{ open: isOpen(r.code) }"
          >
            <button type="button" class="s-hd" @click="toggleExpand(r.code)">
              <span class="s-code">{{ r.code }}</span>
              <span class="s-name">{{ r.name || '—' }}</span>
              <span v-if="r.isSt" class="s-st">ST</span>
              <span class="s-dev" :class="clsOf(r.selDev)">{{ pct(r.selDev) }}</span>
            </button>

            <div class="s-2">
              <span class="ind">{{ r.industry || '未分类' }}</span>
              <span>现价 {{ num(r.price) }}</span>
              <span class="ma">MA{{ win }} {{ num(r['ma' + win]) }}</span>
            </div>

            <div class="s-3">
              <span class="tag" :class="r.bucket">{{ r.bucketLabel }}</span>
              <span v-if="r.excess !== null && r.excess !== undefined" class="ex">
                该档实测 <b :class="clsOf(r.excess)">{{ pct(r.excess) }}</b>
              </span>
              <span v-else class="ghost">该档实测：未测</span>
              <span v-if="r.stableYears >= 4" class="yr">{{ r.stableYears }}/5 年正</span>
              <span class="cv">▾</span>
            </div>

            <div v-show="isOpen(r.code)" class="s-more">
              <div class="kv3">
                <div
                  v-for="c in cellsOf(r)"
                  :key="c.k"
                  class="i"
                  :class="{ on: c.n === win }"
                >
                  <div class="k">{{ c.k }}</div>
                  <div class="v" :class="c.cls">{{ c.v }}</div>
                </div>
              </div>
              <div class="s-note" :class="{ bad: !!missOf(r) }" v-html="noteOf(r)" />
              <div class="s-links">
                <a :href="stockstarCodeLink(r.code)" target="_blank" rel="noopener noreferrer">证券之星</a>
                <a :href="eastmoneyCodeLink(r)" target="_blank" rel="noopener noreferrer">东方财富</a>
              </div>
            </div>
          </div>
        </div>

        <div v-if="hasMore" class="more-btn" @click="loadMore">
          {{ loading ? '加载中…' : `加载更多（已 ${rows.length} / ${total} 只，每页 ${pageSize} 只）` }}
        </div>
        <p v-else class="more-btn done">已到底 · 共 {{ total }} 只</p>
      </template>

      <p class="listnote">
        现价来源：<b>{{ priceSrcSummary }}</b>（<b>逐只判定</b>，不整批套用 —— 库里报价表常是「一半新一半旧」）<br>
        偏离 = 现价 ÷ 均线 − 1；均线用<b>后复权</b>算再折算回真实价。宽表 / 导出请用电脑版访问同一地址。
      </p>
    </template>

    <!-- ==================== 筛选抽屉 ==================== -->
    <van-popup v-model="showFilter" position="bottom" round :style="{ maxHeight: '86%' }">
      <div class="sheet" id="mdev-sheet">
        <div class="handle" />
        <div class="sh">筛选 <button type="button" class="x" @click="showFilter = false">✕</button></div>

        <div class="grouph">样本池（与【波段雷达】同源）</div>
        <div class="cells">
          <div
            v-for="p in poolOrder"
            :key="p"
            class="opt"
            :class="{ on: sample === p }"
            @click="setSample(p)"
          >
            <span class="nm">{{ poolLabels[p] }}</span>
            <span class="n">{{ nf(poolCounts[p]) }} 只可算</span>
            <span class="rd" />
          </div>
        </div>

        <div class="grouph">偏离分档（当前：{{ winLabel }}）</div>
        <div class="chips">
          <span class="chip" :class="{ on: bucket === 'all' }" @click="setBucket('all')">
            全部<span class="c">{{ nf(stats.count) }}</span>
          </span>
          <span
            v-for="g in groups"
            :key="g.key"
            class="chip"
            :class="{ on: bucket === g.key }"
            @click="setBucket(g.key)"
          >
            {{ g.label }}<span class="c">{{ nf(stats.groups[g.key] || 0) }}</span>
          </span>
        </div>

        <div class="switch-row">
          <span class="tx">
            含 ST / 退市股
            <span class="d">
              默认关。全市场池的「深度跌破」档有 <b>31% 是 ST / 退市股</b>
              （实测 144 只里 45 只）；机构池 ST 恒为 0
            </span>
          </span>
          <button
            type="button"
            class="sw"
            :class="{ on: includeSt }"
            aria-label="含 ST / 退市股"
            :aria-pressed="includeSt ? 'true' : 'false'"
            @click="toggleSt"
          />
        </div>

        <div class="ft">
          <button type="button" class="btn" @click="resetFilter">重置</button>
          <button type="button" class="btn primary" @click="showFilter = false">
            看 {{ nf(stats.count) }} 只
          </button>
        </div>
      </div>
    </van-popup>

    <!-- ==================== 分档与实测超额（整页） ==================== -->
    <van-popup v-model="showBacktest" position="bottom" :style="{ height: '100%' }">
      <div class="doc" id="mdev-doc-bt">
        <div class="doc-head">
          <button type="button" class="dback" @click="showBacktest = false">‹ 返回</button>
          <span class="dt">分档与实测超额</span>
        </div>
        <div class="doc-body">
          <div class="bt-head">
            <div class="t">偏离率分布 × 各档实测超额</div>
            <div class="lg">
              {{ sampleLabel }} · {{ nf(stats.count) }} 只 · 基准 {{ maAsOf || '—' }}
              · 分档按 <b>{{ winLabel }}</b><br>
              超额 = 该档未来 20 日<b>相对池等权</b>收益（已剔退市股）；当前列口径
              {{ excessMeasured ? excessKey : '未测' }}
            </div>
          </div>

          <div class="bt-table">
            <div
              v-for="(lb, i) in bucketLabels"
              :key="lb"
              class="brow"
              :class="{ deep: i <= 1, hot: i === 6, chase: i === 12 }"
            >
              <span class="bk">{{ lb }}</span>
              <span class="bar"><i :style="{ width: barW(i) }" /></span>
              <span class="n">{{ nf(dist[i] || 0) }}</span>
              <span class="ex" :class="clsOf(excessAt(i))">{{ excessText(i) }}</span>
              <span class="yr" :class="{ no: !(backtest.stableYears[i] >= 4) }">{{ backtest.stableYears[i] }}/5</span>
            </div>
          </div>

          <div class="bt-lg bt-legend">
            <span><i class="k-deep" /><b>最强</b> −50%~−35%：实测为正且 5/5 年稳</span>
            <span><i class="k-hot" /><b>甜蜜区</b> −3%~+3%：<b>原假设已证伪</b>（≈0 且低于成本 0.08%）</span>
            <span><i class="k-chase" /><b>避雷</b> &gt; +50%：三个配置一致为负</span>
            <span class="dim">条长 = 该档家数占比（按最大档归一到 100%）</span>
          </div>

          <div class="doc-sec">
            <h4>读这张表的三句话</h4>
            <dl>
              <dt>① 单调，不是非单调</dt>
              <dd>偏离越负越好：<b class="up">{{ excessText(1) }}</b>（深跌）→ <b>{{ excessText(6) }}</b>（附近）→ <b class="down">{{ excessText(12) }}</b>（追高）。</dd>
              <dt>② 甜蜜区是空的</dt>
              <dd>
                −3%~+3% 实测 <b>{{ excessText(6) }}</b><template v-if="excessMeasured">，且低于成本 0.08%</template>
                。「回到均线附近会反弹」<b>已被证伪</b>。
              </dd>
              <dt>③ 尾部别当彩票买</dt>
              <dd>
                −∞~−50% 桶只有 {{ nf(dist[0]) }} 只、分年不稳
                —— 高超额 + 少样本 = 先当彩票，不当信号。
              </dd>
            </dl>
          </div>

          <div class="doc-sec">
            <h4>换窗口会怎样</h4>
            <div v-for="n in windowNotes" :key="n.w" class="nrow">
              <div class="nt">
                {{ n.label }}
                <span v-if="!n.measured" class="tag near">未测</span>
              </div>
              <div class="nd">
                <template v-if="!n.measured">
                  该窗口<b>没做过回测</b>，所以超额列显示「未测」—— 页面<b>不会</b>拿年线的数字顶上。
                  要看超额请切到半年线 / 年线。
                </template>
                <template v-else>
                  深跌档 <b class="up">{{ pct(n.deep) }}</b>（5/5 年正）；
                  最极端档 <b class="down">{{ pct(n.tail) }}</b>；
                  追高档 <b class="down">{{ pct(n.chase) }}</b>。
                </template>
              </div>
            </div>
          </div>

          <p class="doc-foot">
            移动端刻意只保留人工复核真正要看的数字；三个已回测配置（{{ excessKeys.join(' / ') }}）
            的逐档对照请在电脑版「分档超额」弹窗里看。
          </p>
        </div>
      </div>
    </van-popup>

    <!-- ==================== 口径与结论（整页） ==================== -->
    <van-popup v-model="showCaliber" position="bottom" :style="{ height: '100%' }">
      <div class="doc" id="mdev-doc-caliber">
        <div class="doc-head">
          <button type="button" class="dback" @click="showCaliber = false">‹ 返回</button>
          <span class="dt">口径与结论</span>
        </div>
        <div class="doc-body">
          <div class="doc-sec">
            <h4><span class="n">1</span>先说结论（口径：{{ backtest.caliber || '—' }}）</h4>
            <dl>
              <dt>把「股价回到中长线均线附近」当买点 → <span class="imp">没有效果</span></dt>
              <dd>
                甜蜜区 −3%~+3% 的未来 20 日相对池等权超额，三个配置分别是 <b>{{ sweetText }}</b>
                —— 全部约等于 0，且全部低于双边成本 0.08%。
              </dd>
              <dt>真正有结构的是「偏离越负越好」</dt>
              <dd>
                深跌档（≤ −35%）实测 <b class="up">{{ pct(backtest.excess['250h20'][1]) }}</b>（年线、5/5 年为正）；
                反过来说「严重追高」（&gt; +50%）三个配置一致
                <b class="down">{{ pct(backtest.excess['250h20'][12]) }}</b>，是最稳的<b>避雷</b>信号。
              </dd>
              <dt>但它与库内已有的 <code>rev60</code> 同源</dt>
              <dd>
                反转因子 <code>all_rev60_h10</code> 年化 +15.07%（全库第 1）。
                若真要赚这个钱，<b>用 rev60 更直接</b>；本页的定位是<b>避雷 + 观察</b>，不是选股推荐。
              </dd>
            </dl>
          </div>

          <div class="doc-sec">
            <h4><span class="n">2</span>偏离怎么算</h4>
            <dl>
              <dt>定义</dt>
              <dd><code>偏离 = 现价 ÷ MA_N − 1</code>；<code>MA_N</code> = 最近 N 个<b>已收盘</b>交易日的（后复权）收盘均值。</dd>
              <dt>⚠️ 复权口径不能省</dt>
              <dd>
                均线必须用<b>后复权</b>价算（不复权会被除权跳空打断）；展示时再把均线按最新收盘折算回真实价：
                <code>MA_real = MA_hfq × k</code>，<code>k = 真实收盘 ÷ 后复权收盘</code>。
                实测该比值 83 个交易日内漂移 <b>2.4%~5.6%</b>，省掉会把茅台算成 −89%（真值 −5.8%）。
              </dd>
            </dl>
          </div>

          <div class="doc-sec">
            <h4><span class="n">3</span>两条更新链路（频率差 1440 倍）</h4>
            <dl>
              <dt>① 均线 MA60 / 120 / 250 —— 每交易日 1 次</dt>
              <dd>线上没有后复权长历史（且总内存仅 1.9G），所以<b>本地算 → 落表 → 推线上</b>。均线是<b>日频常数</b>：一天只变一次。</dd>
              <dt>② 偏离率 —— 每分钟（跟随报价源）</dt>
              <dd><b>请求时现算</b>（一次除法）。<b>刻意不落表</b>：把实时值写死反而会过期。</dd>
            </dl>
          </div>

          <div class="doc-sec">
            <h4><span class="n">4</span>现价来源与新鲜度</h4>
            <dl>
              <dt>逐只判定，不整批套用</dt>
              <dd>
                库里报价表常是「一半新一半旧」。某只的新鲜报价到位就自动改用实时价 ——
                名称与 ST 判定也跟着用同一批口径，避免「价格用今天、名称用上月」。<br>
                当前来源：<b>{{ priceSrcSummary }}</b>
              </dd>
              <dt v-if="!maFresh" class="imp">⚠️ {{ freshHint }}</dt>
            </dl>
          </div>

          <div class="doc-sec">
            <h4><span class="n">5</span>样本与成本</h4>
            <dl>
              <dt>样本池</dt>
              <dd>与【波段雷达】同源：全市场 / Top500 / P60 宽松 / P75 严格。机构池 ST 恒为 0；全市场池的深跌档 <b>31% 是 ST / 退市股</b>。</dd>
              <dt>未覆盖</dt>
              <dd>
                次新股（上市不足 N 个自身交易日）<b>显示「—」而不是 0</b>；停牌股（基准日当天无自身行情）已剔除；北交所三源不供数。
              </dd>
              <dt>成本门槛</dt>
              <dd>双边成本 <code>0.08%</code>。某个档的超额要能站上这条线才谈得上可交易。</dd>
              <dt>研究报告</dt>
              <dd><a :href="reportHref" target="_blank" rel="noopener noreferrer">{{ backtest.report || '—' }}</a></dd>
            </dl>
          </div>

          <p class="doc-foot">
            桌面端的三个浮层（说明 popover / 分档超额弹窗 / 口径弹窗）在移动端合并成<b>两个整页</b>：
            本页 + 「分档与实测超额」。需要 13 列宽表 / 导出 CSV 请用电脑版访问同一地址。
          </p>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { request } from '../utils/http.js'
import { eastmoneyCodeLink, stockstarCodeLink } from '../utils/stockLink.js'

const PAGE_SIZE = 20
const WIN_SHORT = { 60: '季线', 120: '半年线', 250: '年线' }
const WIN_LABEL = { 60: '季线 MA60', 120: '半年线 MA120', 250: '年线 MA250' }
const POOL_ORDER = ['all', 'top500', 'p60', 'p75']
const POOL_LABELS = { all: '全市场', top500: 'Top500', p60: 'P60 宽松', p75: 'P75 严格' }

/** 分档尾句：按分档给，MA60 单独给（该窗口没做过回测） */
const TAIL = {
  deep: '深跌档（≤ −35%）历史实测为正，但样本少、分年不稳 ⇒ 先当<b>观察</b>，别当彩票买。',
  drop: '明显跌破档是「偏离越负越好」这条单调性的中间段，幅度已经很薄。',
  near: '「贴近均线」<b>不是买点</b> —— 甜蜜区 −3%~+3% 实测 ≈0，低于双边成本 0.08%。',
  far: '明显偏离档实测已转负，是追高的第一段。',
  chase: '⚠️ <b>严重追高</b>：三个回测配置一致为负（−0.78% ~ −1.26%）⇒ 本页最稳的<b>避雷</b>结论，不是买点。',
}

export default {
  name: 'MobileMaDeviation',
  data() {
    return {
      loading: false,
      error: '',
      meta: null,
      // 筛选（默认与 PC 端一致）
      sample: 'top500',
      win: 250,
      sort: 'dev_asc',
      bucket: 'all',
      includeSt: false,
      page: 1,
      pageSize: PAGE_SIZE,
      // 数据
      rows: [],
      total: 0,
      stats: { count: 0, stCount: 0, groups: {}, dist: [] },
      poolCounts: {},
      windows: [60, 120, 250],
      groups: [],
      bucketLabels: [],
      backtest: { excess: {}, stableYears: [], sweet: [], report: '', caliber: '' },
      // 元信息
      ready: true,
      maAsOf: '',
      priceAsOf: '',
      lagTradingDays: 0,
      maFresh: true,
      freshHint: '',
      priceSrcCounts: {},
      excessKey: null,
      excessNote: '',
      excessMeasured: true,
      // UI
      expanded: [],
      showOverview: false, // 数据概览默认折叠
      showTip: false,
      showFilter: false,
      showBacktest: false,
      showCaliber: false,
      seq: 0,
    }
  },
  computed: {
    winShort() { return WIN_SHORT },
    winLabel() { return WIN_LABEL[this.win] || String(this.win) },
    sampleLabel() { return POOL_LABELS[this.sample] || this.sample },
    poolOrder() { return POOL_ORDER },
    poolLabels() { return POOL_LABELS },
    dist() { return (this.stats && this.stats.dist) || [] },
    /** 「深度跌破」= 5 档聚合里的 deep（≤ −35%） */
    deepN() { return (this.stats && this.stats.groups && this.stats.groups.deep) || 0 },
    excessKeys() { return Object.keys(this.backtest.excess || {}) },
    distMax() { return Math.max.apply(null, this.dist.concat([1])) || 1 },
    sweetText() {
      const s = this.backtest.sweet || []
      return s.length ? s.map((x) => this.pct(x.excess)).join(' / ') : '—'
    },
    hasMore() { return this.rows.length < this.total },
    listHint() {
      const dir = this.sort === 'dev_asc' ? '升序' : '降序'
      return `按偏离率${dir} · ${this.winLabel}`
    },
    emptyText() {
      if (this.error) return this.error
      if (this.bucket !== 'all') {
        return `「${this.bucketLabelOf(this.bucket)}」在该均线窗口下没有样本 —— 短窗口的偏离天然更小，换一个分档或切到半年线 / 年线`
      }
      return '当前条件下没有可算样本'
    },
    /** ⭐ 现价来源必须**逐只**写清楚（库里报价表常是「一半新一半旧」） */
    priceSrcSummary() {
      const c = this.priceSrcCounts || {}
      const parts = []
      if (c.realtime) parts.push(`实时报价 ${c.realtime}`)
      if (c.daily) parts.push(`日K收盘 ${c.daily}`)
      if (c.build) parts.push(`建表快照 ${c.build}`)
      const when = this.priceAsOf ? ` · ${this.priceAsOf}` : ''
      return parts.length ? parts.join(' / ') + when : `—${when}`
    },
    reportHref() { return this.backtest.report || '' },
    /**
     * 「换窗口会怎样」三行 —— **完全由数据驱动**。
     * ⚠️ 绝不用「窗口号在不在白名单里」这种写法：`excessKeyByWindow[60] === null` 是**业务事实**
     *    （MA60 没跑过回测），只有看 `backtest.excess` 里**有没有那个 key** 才是真相来源。
     */
    windowNotes() {
      const ex = this.backtest.excess || {}
      return [60, 120, 250].map((w) => {
        const key = { 60: null, 120: '120h20', 250: '250h20' }[w]
        const arr = key ? ex[key] : null
        return {
          w, key, label: WIN_LABEL[w], measured: Array.isArray(arr),
          deep: arr ? arr[1] : null, tail: arr ? arr[0] : null, chase: arr ? arr[12] : null,
        }
      })
    },
  },
  created() {
    this.loadMeta().then(() => this.load())
  },
  beforeDestroy() {
    this.seq += 1
  },
  methods: {
    stockstarCodeLink,
    eastmoneyCodeLink,

    /* ---------------- 格式化 ---------------- */
    nf(v) {
      const n = Number(v)
      return Number.isFinite(n) ? n.toLocaleString('en-US') : '—'
    },
    num(v) {
      if (v === null || v === undefined || v === '') return '—'
      const n = Number(v)
      return Number.isFinite(n) ? n.toFixed(2) : '—'
    },
    /** 与 PC 端 `pct()` 同一格式：正数带 +，负数走 ASCII '-' */
    pct(v) {
      if (v === null || v === undefined || v === '' || !Number.isFinite(Number(v))) return '—'
      const n = Number(v)
      return `${n > 0 ? '+' : ''}${n.toFixed(2)}%`
    },
    sharePct(v) {
      if (v === null || v === undefined || !Number.isFinite(Number(v))) return '—'
      return `${(Number(v) * 100).toFixed(1)}%`
    },
    /** 数值着色：正=红、负=绿（A 股口径）；0 与 null 走中性 */
    clsOf(v) {
      if (v === null || v === undefined || !Number.isFinite(Number(v)) || Number(v) === 0) return ''
      return Number(v) > 0 ? 'up' : 'down'
    },
    bucketLabelOf(key) {
      const g = this.groups.find((x) => x.key === key)
      return g ? g.label : key
    },
    excessAt(i) {
      const arr = this.excessKey ? this.backtest.excess[this.excessKey] : null
      return arr && i != null ? arr[i] : null
    },
    excessText(i) {
      return this.excessMeasured ? this.pct(this.excessAt(i)) : '未测'
    },
    barH(n) {
      return `${Math.max(2, Math.round((Number(n) || 0) / this.distMax * 28))}px`
    },
    barW(i) {
      const n = Number(this.dist[i]) || 0
      return `${Math.round((n / this.distMax) * 1000) / 10}%`
    },
    isOpen(code) { return this.expanded.indexOf(code) >= 0 },
    toggleExpand(code) {
      const i = this.expanded.indexOf(code)
      if (i >= 0) this.expanded.splice(i, 1)
      else this.expanded.push(code)
    },
    /**
     * 展开区的六个格子：三条均线（真实价）+ 三个偏离率。
     * ⚠️ 不足 N 个自身交易日 ⇒ 该窗口是 `null` ⇒ 必须显示「—」，**绝不显示 0**（幻影值）。
     */
    cellsOf(r) {
      const mk = (n) => {
        const ma = r['ma' + n]
        const dev = r['dev' + n]
        return [
          { k: `${WIN_LABEL[n].replace(' MA' + n, '')} MA${n}`, n, v: ma == null ? '—' : this.num(ma), cls: ma == null ? 'muted' : '' },
          { k: `偏离 ${n}`, n, v: dev == null ? '—' : this.pct(dev), cls: dev == null ? 'muted' : this.clsOf(dev) },
        ]
      }
      return mk(60).concat(mk(120), mk(250))
    },
    /** 第一个「不可算」的窗口（如 250）—— 用于讲幻影值这条诚实性规则 */
    missOf(r) {
      for (const n of [60, 120, 250]) if (r['ma' + n] == null) return n
      return 0
    },
    noteOf(r) {
      const n = this.missOf(r)
      if (n) {
        return `上市 <b>${r.bars}</b> 个交易日 &lt; ${n} ⇒ <b>${WIN_LABEL[n].replace(' MA' + n, '')}不可算</b>，` +
          '返回 <b>「—」而不是 0</b>。若落 0，这只票会以「偏离 −100%」的幻影值混进深跌档，把该档的平均超额拉歪。'
      }
      // bars 在落表时被截到 250（build-ma-dev.js 的 BAR_CAP）⇒ 只能写「≥250」
      const bars = r.bars >= 250 ? '≥250' : String(r.bars)
      const tail = this.win === 60
        ? '季线 MA60 <b>没做过回测</b> ⇒ 本页不对该窗口做超额断言，要看实测请切到半年线 / 年线。'
        : (TAIL[r.bucket] || '')
      return `三条均线都已可算（上市 <b>${bars}</b> 个交易日）。${tail}`
    },

    /* ---------------- 数据 ---------------- */
    buildQuery(overrides = {}) {
      const p = Object.assign({
        sample: this.sample,
        window: this.win,
        bucket: this.bucket,
        sort: this.sort,
        includeSt: this.includeSt ? '1' : '0',
        page: this.page,
        pageSize: this.pageSize,
      }, overrides)
      return Object.entries(p).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&')
    },
    applyMeta(d) {
      if (!d) return
      if (d.ready === false) {
        this.ready = false
        this.meta = { ready: false, reason: d.reason || '' }
        return
      }
      this.ready = true
      this.maAsOf = d.maAsOf || this.maAsOf
      this.priceAsOf = d.priceAsOf || this.priceAsOf
      this.lagTradingDays = d.lagTradingDays || 0
      this.maFresh = d.maFresh !== false
      this.freshHint = d.freshHint || ''
      if (d.priceSrcCounts) this.priceSrcCounts = d.priceSrcCounts
      if (Array.isArray(d.windows) && d.windows.length) this.windows = d.windows
      if (Array.isArray(d.groups) && d.groups.length) this.groups = d.groups
      if (Array.isArray(d.bucketLabels) && d.bucketLabels.length) this.bucketLabels = d.bucketLabels
      if (d.backtest) this.backtest = d.backtest
    },
    async loadMeta() {
      try {
        const d = await request('/api/ma-deviation/meta')
        this.meta = d || {}
        this.applyMeta(d)
      } catch (e) {
        this.meta = { ready: false, reason: e.message || String(e) }
      }
    },
    /** @param {boolean} append 追加下一页（「加载更多」）；否则重置列表 */
    async load(append = false) {
      if (!this.ready) return
      const seq = ++this.seq
      this.loading = true
      this.error = ''
      try {
        const d = await request(`/api/ma-deviation?${this.buildQuery()}`)
        if (seq !== this.seq) return
        this.applyMeta(d)
        this.rows = append ? this.rows.concat(d.list || []) : (d.list || [])
        this.total = d.total || 0
        this.stats = d.stats || this.stats
        this.poolCounts = d.poolCounts || this.poolCounts
        this.excessKey = d.excessKey || null
        this.excessNote = d.excessNote || ''
        this.excessMeasured = d.excessMeasured !== false
        if (!append) this.expanded = []
      } catch (e) {
        if (seq !== this.seq) return
        if (!append) this.rows = []
        this.error = e.message || '加载年线偏离数据失败'
      } finally {
        if (seq === this.seq) this.loading = false
      }
    },
    loadMore() {
      if (this.loading || !this.hasMore) return
      this.page += 1
      this.load(true)
    },
    /** 任一筛选变化：回到第 1 页重新加载 */
    reload() {
      this.page = 1
      this.load(false)
    },
    setWindow(w) {
      if (w === this.win) return
      this.win = w
      this.reload()
    },
    openCaliberFromTip() {
      this.showTip = false
      this.showCaliber = true
    },
    setSort(s) {
      if (s === this.sort) return
      this.sort = s
      this.reload()
    },
    setSample(p) {
      if (p === this.sample) return
      this.sample = p
      this.reload()
    },
    setBucket(k) {
      if (k === this.bucket) return
      this.bucket = k
      this.reload()
    },
    toggleSt() {
      this.includeSt = !this.includeSt
      this.reload()
    },
    resetFilter() {
      const same = this.sample === 'top500' && this.win === 250 && this.sort === 'dev_asc'
        && this.bucket === 'all' && this.includeSt === false
      this.sample = 'top500'
      this.win = 250
      this.sort = 'dev_asc'
      this.bucket = 'all'
      this.includeSt = false
      if (!same) this.reload()
    },
  },
}
</script>

<style scoped>
.m-ma-dev { padding-bottom: 16px; }

/* ---------- 未就绪 ---------- */
.card { margin: 0 12px; background: #fff; border-radius: 12px; overflow: hidden; }
.not-ready { margin: 12px; padding: 14px; }
.nr-t { font-size: 15px; font-weight: 600; color: #e6a23c; }
.not-ready p { margin: 8px 0 0; font-size: 12.5px; color: #646566; line-height: 1.7; }
.not-ready code { background: #f2f3f5; padding: 0 3px; border-radius: 3px; font-size: 11.5px; }

/* ---------- 均线三档 + 小入口 ---------- */
.toolbar {
  display: flex; align-items: center; gap: 6px; margin: 10px 12px 0;
  padding: 9px 10px 9px 12px; background: #fff; border-radius: 10px; font-size: 13px;
}
.seg { display: flex; background: #f2f3f5; border-radius: 8px; padding: 2px; gap: 2px; }
.seg button {
  border: 0; background: none; font-family: inherit; font-size: 12.5px; color: #646566;
  padding: 5px 9px; border-radius: 6px; white-space: nowrap; cursor: pointer;
}
.seg button.on { background: #fff; color: #1989fa; font-weight: 600; box-shadow: 0 1px 3px rgba(0, 0, 0, .08); }
.toolbar .sp { margin-left: auto; }
.toolbar .tip-ico {
  flex: 0 0 auto; width: 30px; height: 30px; padding: 0; border: 1px solid #f5dab1;
  border-radius: 50%; background: #fdf6ec; color: #e6a23c; font-size: 14px;
  font-family: inherit; line-height: 28px; text-align: center;
}
.toolbar .doc-ico {
  flex: 0 0 auto; height: 30px; padding: 0 9px; border: 1px solid #dcdfe6;
  border-radius: 15px; background: #fff; color: #606266; font-family: inherit;
  font-size: 12px; font-weight: 600; white-space: nowrap;
}
.toolbar .flt {
  border: 0; background: #e8f4ff; color: #1989fa; font-family: inherit; font-size: 12.5px;
  font-weight: 600; padding: 5px 10px; border-radius: 7px; white-space: nowrap;
}

/* ---------- 研究结论短弹层 ---------- */
.tip-pop { padding: 14px 16px 18px; }
.tip-pop-h {
  display: flex; align-items: center; justify-content: space-between;
  font-size: 15px; font-weight: 600; color: #323233; margin-bottom: 10px;
}
.tip-pop-b { font-size: 13px; line-height: 1.7; color: #646566; }
.tip-pop-b p { margin: 0 0 10px; }
.tip-pop-b b { color: #b8860b; }
.tip-pop-b code {
  background: #f2f3f5; padding: 0 3px; border-radius: 3px; font-size: 12px;
}
.tip-more {
  display: block; width: 100%; margin-top: 4px; padding: 10px 0; border: 0;
  background: none; font-family: inherit; font-size: 13px; font-weight: 600;
  color: #1989fa; text-align: center;
}

/* ---------- 排序两端 ---------- */
.sortbar { display: flex; align-items: center; gap: 8px; margin: 9px 12px 0; }
.seg2 { display: flex; background: #f2f3f5; border-radius: 8px; padding: 2px; gap: 2px; }
.seg2 button {
  border: 0; background: none; font-family: inherit; font-size: 12.5px; color: #646566;
  padding: 5px 11px; border-radius: 6px; white-space: nowrap; cursor: pointer;
}
.seg2 button.on { background: #fff; color: #1989fa; font-weight: 600; box-shadow: 0 1px 3px rgba(0, 0, 0, .08); }
.sortbar .cnt { margin-left: auto; font-size: 11.5px; color: #909399; }

/* ---------- 数据概览（默认折叠） ---------- */
.ov {
  margin: 9px 12px 0;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
}
.ov-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 11px 12px;
  border: 0;
  background: none;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: #323233;
  text-align: left;
}
.ov-body { padding: 0 10px 10px; }

/* ---------- 概览 2×2 ---------- */
.grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 7px; margin: 0; }
.stat { background: #f7f8fa; border-radius: 10px; padding: 8px 10px; }
.stat .lb { font-size: 11px; color: #909399; }
.stat .vl { margin-top: 2px; font-size: 18px; font-weight: 700; line-height: 1.22; color: #323233; }
.stat .vl small { margin-left: 2px; font-size: 10.5px; font-weight: 400; color: #909399; }
.stat .sb { margin-top: 2px; font-size: 10.5px; line-height: 1.45; color: #909399; }
.stat .sb b { color: #606266; }
.stat.alert { background: #fef0f0; }
.stat.alert .vl { font-size: 18px; color: #f56c6c; }
.up { color: #f56c6c; }
.down { color: #67c23a; }
.muted { color: #909399; }

/* ---------- 13 档细条 ---------- */
.distmini { margin: 7px 0 0; padding: 9px 11px; background: #f7f8fa; border-radius: 10px; }
.distmini .bar { display: flex; align-items: flex-end; gap: 3px; height: 28px; }
.distmini .bar i { flex: 1; background: #c6e2ff; border-radius: 2px 2px 0 0; display: block; }
.distmini .bar i.hot { background: #f7b9b9; }
.distmini .bar i.chase { background: #b9e2b9; }
.distmini .cap { margin-top: 6px; font-size: 10.5px; line-height: 1.6; color: #909399; }
.distmini .cap b { color: #606266; }

/* ---------- 区块标题 ---------- */
.sec-h { display: flex; align-items: baseline; gap: 7px; margin: 14px 12px 6px; }
.sec-h h3 { margin: 0; font-size: 14.5px; font-weight: 600; color: #323233; }
.sec-h .hint { font-size: 11.5px; color: #909399; }
.sec-h .sp { margin-left: auto; }
.sec-h .act {
  padding: 0; border: 0; background: none; font-family: inherit;
  font-size: 12.5px; font-weight: 600; color: #1989fa;
}

/* ---------- 结果卡片 ---------- */
.pl { padding: 48px 0; }
.errbar {
  margin: 10px 12px; padding: 10px 12px; background: #fef0f0; border-radius: 10px;
  font-size: 12.5px; color: #f56c6c;
}
.stock { padding: 11px 12px; border-bottom: .5px solid #f2f3f5; }
.stock:last-child { border-bottom: 0; }
.s-hd {
  display: flex; align-items: baseline; gap: 6px; width: 100%; padding: 0;
  border: 0; background: none; font-family: inherit; text-align: left;
}
.s-code { font-size: 13.5px; font-weight: 700; letter-spacing: .2px; color: #323233; }
.s-name { font-size: 14px; font-weight: 600; color: #323233; }
.s-st {
  font-size: 10px; color: #f56c6c; border: 1px solid #f7b9b9; background: #fef0f0;
  border-radius: 3px; padding: 0 3px;
}
.s-dev { margin-left: auto; font-size: 17px; font-weight: 700; letter-spacing: .3px; }
.s-2 { display: flex; align-items: center; gap: 6px; margin-top: 4px; font-size: 11.5px; color: #909399; }
.s-2 .ind {
  max-width: 108px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  color: #646566; background: #f4f4f5; border-radius: 4px; padding: 1px 5px;
}
.s-3 { display: flex; align-items: center; gap: 6px; margin-top: 6px; }
.s-3 .ex { font-size: 11.5px; }
.s-3 .ex b, .s-3 .yr { font-variant-numeric: tabular-nums; }
.s-3 .yr {
  font-size: 10.5px; color: #529b2e; background: #f0f9eb; border: 1px solid #e1f3d8;
  border-radius: 3px; padding: 0 4px;
}
.s-3 .ghost { font-size: 11px; color: #c0c4cc; }
.s-3 .cv { margin-left: auto; color: #c0c4cc; font-size: 12px; transition: transform .18s; }
.stock.open .s-3 .cv { transform: rotate(180deg); }

/* 分档标签 */
.tag { display: inline-block; font-size: 10.5px; line-height: 17px; padding: 0 6px; border-radius: 3px; border: 1px solid; white-space: nowrap; }
.tag.deep { color: #f56c6c; border-color: #f7b9b9; background: #fef0f0; }
.tag.drop { color: #e6a23c; border-color: #f5dab1; background: #fdf6ec; }
.tag.near { color: #909399; border-color: #e4e7ed; background: #fafafa; }
.tag.far { color: #1989fa; border-color: #b8dcff; background: #e8f4ff; }
.tag.chase { color: #67c23a; border-color: #b9e2b9; background: #f0f9eb; }

/* ---------- 展开明细 ---------- */
.s-more { margin-top: 9px; padding-top: 9px; border-top: .5px dashed #e4e7ed; }
.kv3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 7px 6px; }
.kv3 .i { background: #f7f8fa; border-radius: 7px; padding: 6px 7px; }
.kv3 .i .k { font-size: 10.5px; color: #909399; }
.kv3 .i .v { font-size: 13px; font-weight: 700; margin-top: 1px; font-variant-numeric: tabular-nums; color: #323233; }
.kv3 .i .v.muted { color: #c0c4cc; }
.kv3 .i.on { background: #ecf5ff; }
.kv3 .i.on .k { color: #79bbff; }
.kv3 .i.on .v { color: #1989fa; }
.s-note {
  margin-top: 9px; background: #fdf6ec; border-radius: 8px; padding: 7px 10px;
  font-size: 11.5px; color: #8a6d3b; line-height: 1.6;
}
.s-note b { color: #b8860b; }
.s-note.bad { background: #fef0f0; color: #c0392b; }
.s-note.bad b { color: #c0392b; }
.s-links { display: flex; gap: 14px; margin-top: 9px; }
.s-links a { font-size: 12.5px; color: #1989fa; text-decoration: none; }

/* ---------- 加载更多 ---------- */
.more-btn {
  margin: 10px 12px 0; padding: 11px 0; text-align: center;
  background: #fff; border-radius: 10px; font-size: 13px; color: #1989fa; font-weight: 600;
}
.more-btn.done { color: #c0c4cc; font-weight: 400; font-size: 11.5px; }
.listnote { margin: 9px 12px 0; font-size: 11px; line-height: 1.7; color: #a8abb2; }
.listnote b { color: #909399; }

/* ---------- 筛选抽屉 ---------- */
.sheet { display: flex; flex-direction: column; max-height: 86vh; padding: 6px 16px 16px; overflow-y: auto; }
.sheet .handle { width: 36px; height: 4px; margin: 6px auto 10px; border-radius: 2px; background: #dcdee0; flex: 0 0 auto; }
.sheet .sh { display: flex; align-items: center; font-size: 16px; font-weight: 600; color: #323233; }
.sheet .sh .x {
  margin-left: auto; padding: 0; border: 0; background: none; font-family: inherit;
  font-size: 18px; line-height: 1; color: #c8c9cc;
}
.grouph { margin-top: 14px; font-size: 12px; color: #909399; }
.cells { margin-top: 7px; border-top: .5px solid #f2f3f5; }
.opt {
  display: flex; align-items: center; gap: 8px; padding: 11px 2px;
  border-bottom: .5px solid #f2f3f5;
}
.opt .nm { font-size: 14px; color: #323233; }
.opt.on .nm { color: #1989fa; font-weight: 600; }
.opt .n { margin-left: auto; font-size: 11.5px; color: #909399; }
.opt .rd {
  flex: 0 0 auto; width: 18px; height: 18px; border-radius: 50%;
  border: 1px solid #dcdee0; position: relative;
}
.opt.on .rd { border-color: #1989fa; background: #1989fa; }
.opt.on .rd::after {
  content: ''; position: absolute; left: 6px; top: 2.5px; width: 4px; height: 8px;
  border: 2px solid #fff; border-top: 0; border-left: 0; transform: rotate(45deg);
}
.chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 7px; }
.chip {
  display: inline-flex; align-items: center; gap: 4px; padding: 5px 9px;
  background: #f4f4f5; border: 1px solid #ebeef5; border-radius: 14px;
  font-size: 12px; color: #646566;
}
.chip.on { background: #e8f4ff; border-color: #b8dcff; color: #1989fa; font-weight: 600; }
.chip .c { font-size: 11px; color: #909399; }
.chip.on .c { color: #1989fa; }
.switch-row {
  display: flex; align-items: center; gap: 10px; margin-top: 14px;
  padding-top: 12px; border-top: .5px solid #f2f3f5;
}
.switch-row .tx { flex: 1; min-width: 0; font-size: 13.5px; color: #323233; }
.switch-row .tx .d { display: block; margin-top: 3px; font-size: 11px; line-height: 1.55; color: #909399; }
.switch-row .tx .d b { color: #f56c6c; }
.sw {
  flex: 0 0 auto; width: 44px; height: 25px; padding: 0; border: 0; border-radius: 13px;
  background: #dcdee0; position: relative; transition: background .18s;
}
.sw::after {
  content: ''; position: absolute; top: 2px; left: 2px; width: 21px; height: 21px;
  border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgba(0, 0, 0, .2);
  transition: transform .18s;
}
.sw.on { background: #1989fa; }
.sw.on::after { transform: translateX(19px); }
.sheet .ft { display: flex; gap: 10px; margin-top: 16px; }
.sheet .ft .btn {
  flex: 1; padding: 11px 0; border: 0; border-radius: 9px;
  font-family: inherit; font-size: 14px; font-weight: 600;
}
.sheet .ft .btn { background: #f2f3f5; color: #646566; }
.sheet .ft .btn.primary { background: #1989fa; color: #fff; }

/* ---------- 两个整页（分档 / 口径） ---------- */
.doc { display: flex; flex-direction: column; height: 100%; background: #fff; }
.doc-head {
  flex: 0 0 auto; display: flex; align-items: center; height: 46px; padding: 0 12px;
  border-bottom: .5px solid #ebedf0; position: relative;
}
.dback { padding: 0; border: 0; background: none; font-family: inherit; font-size: 14px; color: #1989fa; }
.doc-head .dt {
  position: absolute; left: 0; right: 0; text-align: center; font-size: 16px;
  font-weight: 600; color: #323233; pointer-events: none;
}
.doc-body { flex: 1 1 auto; min-height: 0; overflow-y: auto; -webkit-overflow-scrolling: touch; padding: 0 14px 24px; }

.bt-head { padding: 12px 0 0; }
.bt-head .t { font-size: 14.5px; font-weight: 600; color: #323233; }
.bt-head .lg { margin-top: 5px; font-size: 11px; line-height: 1.7; color: #909399; }
.bt-head .lg b { color: #606266; }

.bt-table { margin-top: 10px; }
.brow {
  display: grid; grid-template-columns: 74px minmax(0, 1fr) 34px 54px 34px;
  align-items: center; gap: 6px; padding: 6px 7px; border-radius: 6px;
}
.brow.deep { background: #fef0f0; }
.brow.hot { background: #fdf6ec; }
.brow.chase { background: #f0f9eb; }
.brow .bk { font-size: 10.5px; color: #606266; white-space: nowrap; }
.brow .bar { height: 10px; background: #f0f2f5; border-radius: 5px; overflow: hidden; }
.brow .bar i { display: block; height: 100%; background: #79bbff; border-radius: 5px; }
.brow.deep .bar i { background: #f7b9b9; }
.brow.hot .bar i { background: #f5dab1; }
.brow.chase .bar i { background: #b9e2b9; }
.brow .n { font-size: 11px; color: #909399; text-align: right; font-variant-numeric: tabular-nums; }
.brow .ex { font-size: 11px; font-weight: 600; text-align: right; font-variant-numeric: tabular-nums; }
.brow .yr {
  font-size: 10px; text-align: center; color: #529b2e;
  background: #f0f9eb; border: 1px solid #e1f3d8; border-radius: 3px; padding: 1px 0;
}
.brow .yr.no { color: #c0c4cc; background: #fafafa; border-color: #ebeef5; }
.bt-legend { display: flex; flex-direction: column; gap: 5px; margin-top: 10px; font-size: 11px; line-height: 1.6; color: #606266; }
.bt-legend span { display: flex; align-items: flex-start; gap: 6px; }
.bt-legend i { flex: 0 0 auto; width: 9px; height: 9px; margin-top: 4px; border-radius: 2px; }
.k-deep { background: #f7b9b9; }
.k-hot { background: #f5dab1; }
.k-chase { background: #b9e2b9; }
.bt-legend .dim { color: #a8abb2; }

.doc-sec { margin-top: 16px; }
.doc-sec h4 {
  margin: 0 0 6px; font-size: 13.5px; font-weight: 600; color: #323233;
  display: flex; align-items: baseline; gap: 6px;
}
.doc-sec h4 .n {
  flex: 0 0 auto; width: 16px; height: 16px; border-radius: 50%; background: #e8f4ff;
  color: #1989fa; font-size: 10px; font-weight: 700; line-height: 16px; text-align: center;
}
.doc-sec dl { margin: 0; }
.doc-sec dt { margin-top: 8px; font-size: 12.5px; font-weight: 600; color: #606266; }
.doc-sec dt .imp { color: #f56c6c; }
.doc-sec dd { margin: 3px 0 0; font-size: 12px; line-height: 1.75; color: #606266; }
.doc-sec dd b { color: #303133; }
.doc-sec dd .up { font-weight: 600; }
.doc-sec code { background: #f2f3f5; border-radius: 3px; padding: 0 3px; font-size: 11.5px; }
.doc-sec .tag.near { vertical-align: middle; }
.nrow { margin-top: 8px; }
.nrow .nt { font-size: 12.5px; font-weight: 600; color: #606266; }
.nrow .nd { margin-top: 2px; font-size: 12px; line-height: 1.75; color: #606266; }
.nrow .nd b { color: #303133; }
.doc-foot { margin: 18px 0 0; font-size: 11px; line-height: 1.7; color: #a8abb2; }
</style>

<!-- 项目没有全局 box-sizing 重置：不补这一块，卡片会被 padding + border 撑宽导致横向溢出 -->
<style>
.m-ma-dev,
.m-ma-dev * {
  box-sizing: border-box;
}
</style>
